// src/pages/project/AssetSetup.jsx
//
// One asset's specification, as tabs: Site & General, PV System, Wind System,
// BESS, Grid Connection. The tab bodies are the existing setup pages, which
// keep editing `history.present` - the active asset's config - so nothing in
// them had to change. A single slim bar carries the tabs and the asset
// selector; the pages keep their own titles.

import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Stack, Tab, Tabs } from "@mui/material";
import { useFile } from "../../context/FileContext";
import { useHistory } from "../../context/HistoryContext";
import { useDemo } from "../../context/DemoContext";
import { getDemoWindSystemDisabledMessage } from "../../utils/demoAccess";
import { useLanguage } from "../../context/LanguageContext";
import AssetSelect from "../../components/AssetSelect";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import WindPowerOutlinedIcon from "@mui/icons-material/WindPowerOutlined";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import { SolarBoltIcon } from "../../components/customTabIcons";
import General from "../General";
import PVSetup from "../PVSetup";
import WindSetup from "../WindSetup";
import BESSSetup from "../BESSSetup";
import GridSetup from "../GridSetup";

import { assetTypeDefinition, assetTypeIssue } from "../../utils/projectAssets";

export const ASSET_TABS = [
  { key: "general", labelKey: "nav.siteGeneral", label: "Site & General", icon: <AssignmentOutlinedIcon />, element: <General /> },
  { key: "pv", labelKey: "nav.pvSystem", label: "PV System", icon: <SolarBoltIcon />, element: <PVSetup /> },
  { key: "wind", labelKey: "nav.windSystem", label: "Wind System", icon: <WindPowerOutlinedIcon />, element: <WindSetup /> },
  { key: "bess", labelKey: "nav.bess", label: "BESS", icon: <BatteryChargingFullIcon />, element: <BESSSetup /> },
  { key: "grid", labelKey: "nav.gridConnection", label: "Grid Connection", icon: <ElectricalServicesIcon />, element: <GridSetup /> },
];

/** Tabs that apply to this asset's system type. */
export function visibleAssetTabs(cfg) {
  const type = assetTypeDefinition(cfg?.systemType);
  return ASSET_TABS.filter((t) => (t.key === "pv" ? type?.hasPv : t.key === "wind" ? type?.hasWind : t.key === "bess" ? type?.hasBess : true));
}

export default function AssetSetup() {
  const { t: translate } = useLanguage();
  const { activeAsset } = useFile();
  const history = useHistory();
  const location = useLocation();
  const navigate = useNavigate();
  const { demoProfile } = useDemo();
  const windDisabledMessage = getDemoWindSystemDisabledMessage(demoProfile);

  const tabs = useMemo(
    () => visibleAssetTabs(history.present).filter((tab) => tab.key !== "wind" || !windDisabledMessage),
    [history.present, windDisabledMessage]
  );
  const requested = new URLSearchParams(location.search).get("tab");
  const current = tabs.find((t) => t.key === requested) || tabs[0];

  useEffect(() => {
    if (current?.key !== "general" || location.hash !== "#btm-capacity-charge") return undefined;
    const frame = requestAnimationFrame(() => {
      document.getElementById("btm-capacity-charge")?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [current?.key, location.hash]);

  return (
    <Box>
      {/* One slim bar: which asset, which part of it. The page below keeps its own title. */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 1, pt: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
      >
        <Tabs
          value={current?.key}
          onChange={(_, v) => navigate({ pathname: location.pathname, search: `?tab=${v}` }, { replace: true })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, minWidth: 0, "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 44, minWidth: "auto", px: 1, justifyContent: "flex-start" }, "& .MuiTab-icon": { fontSize: 20 } }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} value={t.key} label={translate(t.labelKey)} icon={t.icon} iconPosition="start" />
          ))}
        </Tabs>
        <Box sx={{ pb: { xs: 1, md: 0.75 } }}>
          <AssetSelect minWidth={300} maxWidth={560} sx={{ width: "auto" }} />
        </Box>
      </Stack>
      {assetTypeIssue(history.present) && <Alert severity="warning" sx={{ m: 2 }}>{assetTypeIssue(history.present)}</Alert>}
      {requested === "wind" && windDisabledMessage && <Alert severity="info" sx={{ m: 2 }}>{windDisabledMessage}</Alert>}
      <Box key={`${activeAsset?.id || "none"}-${current?.key}`}>{current?.element}</Box>
    </Box>
  );
}
